import React from 'react';

/**
 * Un componente de toggle (interruptor) con etiquetas personalizables para los estados true/false.
 * Es ideal para opciones binarias como 'Cliente' vs 'Nosotros', 'Sí' vs 'No', etc.
 *
 * @param {string} label - La etiqueta principal que se muestra encima del toggle.
 * @param {boolean} value - El valor booleano actual que determina el estado del toggle.
 * @param {function(boolean): void} onChange - La función que se llama con el nuevo valor (true/false) cuando el toggle cambia.
 * @param {string} falseLabel - La etiqueta que se muestra a la izquierda (cuando el valor es `false`).
 * @param {string} trueLabel - La etiqueta que se muestra a la derecha (cuando el valor es `true`).
 * @param {boolean} [disabled=false] - Si es `true`, el toggle se deshabilita.
 */
const EditableBoolean = ({
  label,
  value,
  onChange,
  falseLabel = "NO",
  trueLabel = "SI",
  disabled = false,
}) => {
  
  const handleToggleChange = (event) => {
    // Llama a la función del padre solo si no está deshabilitado
    if (!disabled && onChange) {
      onChange(event.target.checked);
    }
  };

  return (
    <div>
      {/* Etiqueta principal */}
      <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
        {label}
      </label>
      
      {/* Contenedor del widget */}
      <div className="flex items-center justify-center gap-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
        
        {/* Etiqueta para el estado 'false' (izquierda) */}
        <span className={`text-sm font-medium transition-colors duration-200 ${
          !value ? 'text-blue-600 font-semibold' : 'text-gray-500'
        }`}>
          {falseLabel}
        </span>
        
        {/* El interruptor (toggle) */}
        <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={value}
            onChange={handleToggleChange}
            className="sr-only peer" // Oculta el checkbox por defecto, lo estilizamos con el div
            disabled={disabled}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
        
        {/* Etiqueta para el estado 'true' (derecha) */}
        <span className={`text-sm font-medium transition-colors duration-200 ${
          value ? 'text-blue-600 font-semibold' : 'text-gray-500'
        }`}>
          {trueLabel}
        </span>
      </div>
    </div>
  );
};

export default EditableBoolean;
